import React from 'react';
import { Divider, Stack } from '@mui/material';

import { PropertyTextInput } from '@paragon/client-tools';
import { FeaturePatternTrussContainer } from '@paragon/contract-library';

import { TransformationButtons } from '../../../../library/transformations/controls/TransformationButtons';
import { FeaturePatternContainerPatchOrUpdateFn } from '../../../../store/actions/layout/featurePatternContainerActions';
import {
  featurePatternContainerElevationsMatch,
  getFeaturePatternContainerElevationsFromActiveLevel,
} from '../../../../store/selectors/layoutDataSelectors';
import { FeaturePatternContainerShapeControls } from '../../../controls/feature-pattern-containers/FeaturePatternContainerShapeControls';
import { FeaturePatternContainerSlopeControls } from '../../../controls/feature-pattern-containers/FeaturePatternContainerSlopeControls';
import { useAppSelector } from '../../../hooks';
import { LabeledBox } from './LabeledBox';
import { ProjectHierarchyContextSelects } from './ProjectHierarchyContextSelects';
import { SyncElevationButton } from './SyncElevationButton';

interface Props {
  readonly disabled?: boolean;
  readonly showMovementControls?: boolean;
  readonly hideSlopeControls?: boolean;

  readonly value: Omit<FeaturePatternTrussContainer, 'guid'>;
  readonly onChange: (changes: FeaturePatternContainerPatchOrUpdateFn) => void;
}

/**
 * Note that these controls are used in the properties panel for feature pattern containers. We strongly desire to keep
 *   them as visually similar to the `BulkFeaturePatternContainerProperties` panel as possible.  Please consider
 *   updating that panel as well when making any changes here.
 */
export function FeaturePatternContainerPropertyControls({
  disabled,
  showMovementControls,
  hideSlopeControls,
  value,
  onChange,
}: Props) {
  const elevationsFromLevel = useAppSelector(getFeaturePatternContainerElevationsFromActiveLevel);

  return (
    <Stack spacing={2}>
      {showMovementControls && (
        <>
          <TransformationButtons disabled={disabled} />
          <Divider />
        </>
      )}

      <PropertyTextInput
        fullWidth
        sx={{ marginBottom: 'unset!important' }}
        property="Name"
        value={value.name}
        submit={(name) => {
          onChange({ name });
        }}
        disabled={disabled}
      />

      <ProjectHierarchyContextSelects
        levelGuid={value.levelGuid}
        onChange={(levelGuid) => onChange({ levelGuid })}
        disabled={disabled}
      />

      <SyncElevationButton
        plural // because feature pattern containers have a top and bottom elevation
        disabled={disabled}
        visible={
          value.levelGuid != null &&
          elevationsFromLevel.topElevation != null &&
          elevationsFromLevel.bottomElevation != null
        }
        synchronized={featurePatternContainerElevationsMatch(value, elevationsFromLevel)}
        onSynchronize={() => {
          onChange({ shape: { ...value.shape, ...elevationsFromLevel } });
        }}
      />

      <FeaturePatternContainerShapeControls
        value={value.shape}
        onChange={(shape) => onChange({ shape })}
        disabled={disabled}
      />

      {!hideSlopeControls && (
        <LabeledBox title="Slope" contentSx={{ padding: 1 }}>
          <FeaturePatternContainerSlopeControls disabled={disabled} value={value.slopeSettings} onChange={onChange} />
        </LabeledBox>
      )}
    </Stack>
  );
}
